import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { getHistoricSystemData } from '../../api/Optimise';
import { AppContext } from '../../context/AppContext';
import withPageTitle from '../../hocs/withPageTitle';
import { useOnDateChange } from '../../hooks';
import type { ChartDataPoint, SystemEnergyHistoryDataItem } from '../../types/charts';
import type { OptimiseUserDetails } from '../../types/optimise';
import BatteryStateOfCharge from '../../ui/Dashboard/EnergyFlow/BatteryStateOfCharge';
import SystemEnergyHistoryChart from '../../ui/Dashboard/EnergyFlow/SystemEnergyHistoryChart';
import InfoModal from '../../ui/Modal/InfoModal';
import SmartTariffWarning from '../../ui/SmartTariffWarning';
import WarningPanel from '../../ui/WarningPanel';
import { HttpError, UserSub } from '../../utils/apiRequest';
import { calculateYDomain, processSocChartData, processSystemEnergyHistoryChartData } from '../../utils/charts';
import {
  addDays,
  endOfDayInTz,
  fromUnixTime,
  getUnixTime,
  isAfter,
  isBefore,
  isSameDayInTz,
  SITE_TIMEZONE,
  startOfDayInTz,
  subDays,
} from '../../utils/dateUtils';

interface EnergyFlowAppContextType {
  analyticsStartDate: string | null;
  setDatePickerWarning: (warning: React.ReactNode | undefined) => void;
  userSub: UserSub;
  userDetails: OptimiseUserDetails | null;
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
}

const energyFlowAppContext = AppContext as React.Context<EnergyFlowAppContextType>;

type SystemEnergyHistoryChartOwnProps = {
  data: SystemEnergyHistoryDataItem[];
  fetchingAnalyticsData: boolean;
  yDomain: [number, number];
  onPreviousDay: () => void;
  onNextDay: () => void;
  isAtMinDate: boolean;
  isAtMaxDate: boolean;
  selectedDate: Date;
};

type BatteryStateOfChargeOwnProps = {
  data: ChartDataPoint[];
  fetchingAnalyticsData: boolean;
  onPreviousDay: () => void;
  onNextDay: () => void;
  isAtMinDate: boolean;
  isAtMaxDate: boolean;
  selectedDate: Date;
};

const SystemEnergyHistoryChartView = SystemEnergyHistoryChart as React.ComponentType<SystemEnergyHistoryChartOwnProps>;
const BatteryStateOfChargeView = BatteryStateOfCharge as React.ComponentType<BatteryStateOfChargeOwnProps>;

const EnergyFlowContainer = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [fetchingAnalyticsData, setFetchingAnalyticsData] = useState(false);
  const [socChartData, setSocChartData] = useState<ChartDataPoint[]>([]);
  const [systemEnergyHistoryChartData, setSystemEnergyHistoryChartData] = useState<SystemEnergyHistoryDataItem[]>([]);
  const [yDomain, setYDomain] = useState<[number, number]>([0, 0]);
  const [showNoDataModal, setShowNoDataModal] = useState(false);
  const { analyticsStartDate, setDatePickerWarning, userSub, userDetails, selectedDate, setSelectedDate } =
    useContext(energyFlowAppContext);

  const isLoadingUserDetails = !userDetails;
  const appName = theme.language.appName;

  // Date range constraints from userDetails for arrow navigation
  const minDate = userDetails?.plant_data_range?.start_datetime
    ? fromUnixTime(userDetails.plant_data_range.start_datetime)
    : null;
  const maxDate = userDetails?.plant_data_range?.end_datetime
    ? fromUnixTime(userDetails.plant_data_range.end_datetime)
    : subDays(new Date(), 1);

  const isAtMinDate = minDate ? !isAfter(selectedDate, minDate) : false;
  const isAtMaxDate = !isBefore(selectedDate, maxDate);

  const handlePreviousDay = useCallback(() => {
    if (!isAtMinDate && selectedDate) {
      const prevDay = subDays(selectedDate, 1);
      setSelectedDate(prevDay);
    }
  }, [selectedDate, setSelectedDate, isAtMinDate]);

  const handleNextDay = useCallback(() => {
    if (!isAtMaxDate && selectedDate) {
      const nextDay = addDays(selectedDate, 1);
      setSelectedDate(nextDay);
    }
  }, [selectedDate, setSelectedDate, isAtMaxDate]);

  const startDateIsToday = !!analyticsStartDate
    ? isSameDayInTz(SITE_TIMEZONE, new Date(analyticsStartDate), new Date())
    : true;

  const updateAnalyticsData = useCallback(
    async (day?: Date | null) => {
      const baseDate = day ?? subDays(new Date(), 1);
      const startDate = startOfDayInTz(SITE_TIMEZONE, baseDate);
      const endDate = endOfDayInTz(SITE_TIMEZONE, baseDate);
      const startTimestamp = getUnixTime(startDate);
      const endTimestamp = getUnixTime(endDate);
      try {
        setFetchingAnalyticsData(true);
        const analyticsData = await getHistoricSystemData(startTimestamp, endTimestamp, userSub);
        setSocChartData(processSocChartData(analyticsData, SITE_TIMEZONE, baseDate));
        const processedSystemData = processSystemEnergyHistoryChartData(analyticsData, SITE_TIMEZONE);
        setSystemEnergyHistoryChartData(processedSystemData);
        calculateYDomain(processedSystemData, setYDomain);
      } catch (e: unknown) {
        const status = e && typeof e === 'object' && 'status' in e ? (e as HttpError).status : undefined;
        if (status === 404) {
          setSocChartData([]);
          setSystemEnergyHistoryChartData([]);
        }
        console.error(e);
      } finally {
        setFetchingAnalyticsData(false);
      }
    },
    [userSub]
  );

  useEffect(() => {
    if (startDateIsToday) {
      setDatePickerWarning(
        <>
          <WarningPanel
            title="Not enough data to show your Energy Flow."
            body={`Please wait 24 hours for ${appName} to collect your historic data.`}
            linkText={`Why does ${appName} not have my data yet?`}
            linkAction={() => setShowNoDataModal(true)}
          />
        </>
      );
    } else {
      setDatePickerWarning(undefined);
    }
  }, [startDateIsToday, setDatePickerWarning, appName]);

  const onSelectDate = useCallback(
    (date: Date) => {
      void updateAnalyticsData(date);
      void navigate(`/app/energy-flow/${getUnixTime(date)}`);
    },
    [navigate, updateAnalyticsData]
  );

  useOnDateChange(onSelectDate);

  return (
    <>
      {!userDetails?.has_smart_tariff && !isLoadingUserDetails && (
        <SmartTariffWarning
          linkText="Open tariff recommender."
          linkAction={() => {
            void navigate(`/app/tariffs`);
          }}
        />
      )}

      <SystemEnergyHistoryChartView
        data={systemEnergyHistoryChartData}
        fetchingAnalyticsData={fetchingAnalyticsData}
        yDomain={yDomain}
        onPreviousDay={handlePreviousDay}
        onNextDay={handleNextDay}
        isAtMinDate={isAtMinDate}
        isAtMaxDate={isAtMaxDate}
        selectedDate={selectedDate}
      />
      <BatteryStateOfChargeView
        data={socChartData}
        fetchingAnalyticsData={fetchingAnalyticsData}
        onPreviousDay={handlePreviousDay}
        onNextDay={handleNextDay}
        isAtMinDate={isAtMinDate}
        isAtMaxDate={isAtMaxDate}
        selectedDate={selectedDate}
      />
      <InfoModal
        show={showNoDataModal}
        onClose={() => setShowNoDataModal(false)}
        infoContent={
          <div>
            Calculating energy flow can be a complex and time-consuming process. For this reason, {appName} performs
            these calculations once a day to provide a summary from the previous day. <br />
            <br />
            So, when you first sign up, you may need to wait up to 24 hours for energy flow to be available.
          </div>
        }
      />
    </>
  );
};

export default withPageTitle('Energy Flow', EnergyFlowContainer);
